import { Controller } from '@hotwired/stimulus'

/**
 * MFA / OTP Code Entry Controller
 *
 * Provides a segmented, 6-digit MFA input experience that feels native on desktop and mobile:
 * - Six "cell" inputs (one digit each) with auto-advance and keyboard navigation.
 * - Paste and mobile autofill support (e.g., pasting "123456" fills all cells).
 * - A hidden input (named `code`) is kept in sync so Rails receives params[:code] as a single value.
 * - Submit button enable/disable + style toggle based on whether all digits are present.
 * - Inline error styling that can be set by the server and cleared immediately on user interaction.
 *
 * Expected DOM structure (targets must be descendants of the controller element):
 * - panel: container for the MFA input region (used for error styling)
 * - cell: 6 individual inputs (one character each)
 * - hidden: hidden <input name="code"> which is submitted to Rails
 * - submit: the submit control that should be enabled only when the code is complete
 * - errorMessage (optional): element containing server-provided inline error text
 *
 * Controller values:
 * - lengthValue: number of digits expected (defaults to 6)
 * - errorValue: boolean indicating a server-rendered error state
 *
 * Notes:
 * - Values (data-mfa-code-*-value) must live on the same element as data-controller="mfa-code".
 * - Targets (data-mfa-code-target="*") must be descendants of the controller element.
 */
export default class extends Controller {
  static targets = ['panel', 'cell', 'hidden', 'submit', 'errorMessage']

  static values = {
    length: { type: Number, default: 6 },
    error: Boolean,
  }

  /**
   * Called when the controller is connected to the DOM.
   * Initializes the segmented cells from any pre-existing hidden value (Turbo restore / re-render),
   * synchronizes the hidden field, updates submit state, and applies server error styling if present.
   */
  connect() {
    // If Rails re-renders with a value (or Turbo restores), reflect it in the cells.
    const existing = (this.hiddenTarget.value || '').trim()
    if (existing) {
      this.fillFromString(existing, 0)
      this.updateAllCellClasses()
    }

    // Tracks the previous "filled" state to avoid repeatedly stealing focus.
    this.wasFilled = false

    this.sync()
    this.updateSubmit()

    if (this.errorValue) this.applyErrorStyles()
  }


  onFocus(e) {
    const el = e.currentTarget
    const idx = this.cellTargets.indexOf(el)

    // Find first empty cell
    const firstEmptyIndex = this.cellTargets.findIndex((cell) => !cell.value)

    // If user clicks ahead of where they should be typing,
    // force focus to the correct starting position
    if (firstEmptyIndex !== -1 && idx > firstEmptyIndex) {
      requestAnimationFrame(() => {
        this.cellTargets[firstEmptyIndex].focus()
      })
      return
    }

    /**
     * Keeps caret placement stable in single-character cells.
     * Some browsers can select/position caret unexpectedly; this normalizes the experience.
     */
    requestAnimationFrame(() => el.setSelectionRange?.(1, 1))
  }

  /**
   * Handles user input in any cell.
   *
   * Behavior:
   * - Clears server error state as soon as the user edits.
   * - Filters non-digits.
   * - If multiple digits arrive at once (paste/autofill), distributes across cells.
   * - On single digit entry, advances focus to the next cell (if any).
   * - Syncs hidden field and updates submit state after each change.
   */
  onInput(e) {
    this.clearErrorStyles()

    const el = e.currentTarget
    const idx = this.cellTargets.indexOf(el)

    const raw = el.value || ''
    const digits = raw.replace(/\D/g, '')

    // Mobile autofill / paste can insert multiple digits into a single cell.
    if (digits.length > 1) {
      this.fillFromString(digits, idx)
      this.sync()
      this.updateAllCellClasses()
      this.updateSubmit()
      return
    }

    // Enforce single-digit constraint per cell.
    el.value = digits
    this.updateCellClass(el)

    // Auto-advance once a digit is entered (except for the last cell).
    // 1) If an empty cell exists *after* the current index, focus the first one.
    // 2) Otherwise, fall back to the immediate next cell (idx + 1), if any.
    if (digits) {
      const nextEmptyAfter = this.cellTargets.findIndex((cell, i) => i > idx && !cell.value)

      if (nextEmptyAfter !== -1) {
        this.cellTargets[nextEmptyAfter].focus()
      } else if (idx < this.lengthValue - 1) {
        this.cellTargets[idx + 1].focus()
      }
    }

    this.sync()
    this.updateSubmit()
  }

  /**
   * Handles keyboard navigation.
   *
   * - Backspace clears current cell; if already empty, moves to previous cell and clears it.
   * - Arrow keys move focus left/right between cells.
   */
  onKeydown(e) {
    const el = e.currentTarget
    const idx = this.cellTargets.indexOf(el)

    if (e.key === 'Backspace') {
      // If current cell has a digit, clear it in place.
      if (el.value) {
        el.value = ''
        this.updateCellClass(el)
        this.sync()
        this.updateSubmit()
        return
      }

      // If empty, move back and clear the previous cell.
      if (idx > 0) {
        e.preventDefault()
        const prev = this.cellTargets[idx - 1]
        prev.value = ''
        this.updateCellClass(prev)
        prev.focus()
        this.sync()
        this.updateSubmit()
      }
      return
    }

    if (e.key === 'ArrowLeft' && idx > 0) {
      e.preventDefault()
      this.cellTargets[idx - 1].focus()
    }

    if (e.key === 'ArrowRight' && idx < this.lengthValue - 1) {
      e.preventDefault()
      this.cellTargets[idx + 1].focus()
    }
  }

  /**
   * Handles paste explicitly to support users pasting the full OTP into any cell.
   * Filters to digits only, then distributes across cells starting at the focused index.
   */
  onPaste(e) {
    this.clearErrorStyles()

    e.preventDefault()
    const el = e.currentTarget
    const idx = this.cellTargets.indexOf(el)

    const text = (e.clipboardData || window.clipboardData).getData('text') || ''
    const digits = text.replace(/\D/g, '')
    if (!digits) return

    this.fillFromString(digits, idx)
    this.updateAllCellClasses()
    this.sync()
    this.updateSubmit()
  }

  /**
   * Distributes a digit string across the segmented cells.
   * Example: fillFromString("123456", 0) fills all cells from the first.
   *
   * @param {string} digits - input string (digits will be sanitized)
   * @param {number} startIndex - index to start filling from
   */
  fillFromString(digits, startIndex = 0) {
    const onlyDigits = (digits || '').replace(/\D/g, '')
    const arr = onlyDigits.split('').slice(0, this.lengthValue - startIndex)

    for (let i = 0; i < arr.length; i++) {
      const targetIndex = startIndex + i
      if (targetIndex >= this.lengthValue) break
      this.cellTargets[targetIndex].value = arr[i]
    }

    // Focus next index (or last cell) to keep a natural flow.
    const nextIndex = Math.min(startIndex + arr.length, this.lengthValue - 1)
    this.cellTargets[nextIndex]?.focus()
  }

  /**
   * Applies "filled" styling to a single cell based on whether it has a value.
   */
  updateCellClass(cell) {
    cell.classList.toggle('cell-filled', !!cell.value)
  }

  /**
   * Applies "filled" styling across all cells. Useful after bulk operations (paste/autofill).
   */
  updateAllCellClasses() {
    this.cellTargets.forEach((cell) => this.updateCellClass(cell))
  }

  /**
   * Adds error styling on the panel wrapper. Intended to be triggered by a server-rendered error.
   * Styling is applied to the wrapper so CSS can cascade to cells/message as needed.
   */
  applyErrorStyles() {
    if (!this.hasPanelTarget) return
    this.panelTarget.classList.add('mfa-code--error')
  }

  /**
   * Clears error styling and removes the error message node (if present).
   * This is called proactively on input/paste so the UI responds immediately to user correction.
   */
  clearErrorStyles() {
    if (!this.errorValue) return
    this.errorValue = false

    if (this.hasPanelTarget) {
      this.panelTarget.classList.remove('mfa-code--error')
    }

    if (this.hasErrorMessageTarget) {
      this.errorMessageTarget.remove()
    }
  }

  /**
   * Synchronizes the hidden input value from the segmented cells so Rails receives a single `code`.
   * Example: ["1","2","3","4","5","6"] => "123456"
   */
  sync() {
    const value = this.cellTargets.map((c) => (c.value || '').replace(/\D/g, '')).join('')

    this.hiddenTarget.value = value
  }

  /**
   * Enables/disables and styles the submit button based on whether the code is complete.
   * Also focuses the submit button when the user first reaches a complete code.
   *
   * Focus behavior is intentionally edge-triggered (only on transition to "filled") to avoid
   * repeatedly stealing focus on subsequent updates.
   */
  updateSubmit() {
    const value = (this.hiddenTarget.value || '').trim()
    const filled = value.length === this.lengthValue

    // Update disabled state and visual styling first.
    this.submitTarget.disabled = !filled
    this.submitTarget.classList.add('dome-btn', 'dome-btn-base')
    this.submitTarget.classList.toggle('dome-btn-dark-green', filled)
    this.submitTarget.classList.toggle('dome-btn-disabled', !filled)

    // Focus the submit button once when transitioning from incomplete -> complete.
    if (filled && !this.wasFilled) {
      requestAnimationFrame(() => this.submitTarget.focus())
    }

    this.wasFilled = filled
  }
}
